import {
  EquipmentAssignmentRecord,
  LecturerAssignmentRecord,
  LocationAssignmentRecord,
} from "organisms/EventResourcePlanner/EventResourcePlanner.types";
import { EventLocationRecordType } from "pages/EventLocation/EventLocation.types";
import { ResponsibilityRecordType } from "pages/ResponsibilityType/ResponsibilityType.types";
import {
  CustomFieldDataType,
  CustomFieldMetaType,
  FileRecordType,
  LecturerRecordType,
  MinimalUserRecordType,
  NonStaffFileRecordType,
  PersonRecordType,
  PriceRecordType,
  TodoRecordType,
} from "pages/SharedRecords.types";
import { Identifier } from "react-admin";

export type EventDateRecordType = {
  created_at: string;
  day: string;
  end_time: string | null;
  end_datetime: string;
  id: Identifier;
  start_time: string | null;
  start_datetime: string;
  updated_at: string;
  format?: string | null;
  meta?: { location_name: string | null };
  lecturer_assignments?: LecturerAssignmentRecord[];
  location_assignments?: LocationAssignmentRecord[];
  equipment_assignments?: EquipmentAssignmentRecord[];
  /** Write-only counterparts of the assignment lists above, set by EventDateForm. */
  assigned_lecturers?: Identifier[];
  assigned_equipment?: Identifier[];
  assigned_location?: Identifier | null;
  assigned_rooms?: Identifier[];
};

export type EventLecturerRecordType = {
  id: number;
  user: number;
};

export type EventMetadataType = {
  all_valid_bookings_count: number;
  all_waiting_bookings_count: number;
  contact_persons: PersonRecordType[] | null;
  event_base_category_names: string[];
  event_base_name: string;
  event_base_type: number | null;
  event_base_type_name: string | null;
  event_location_name: string | null;
  event_location_rooms: string[];
  group_parent_id: number | null;
  group_parent_name: string | null;
  is_group_parent: boolean;
  is_event_base_group_parent: boolean;
  interests_count: number;
  unprocessed_enrollments_count: number;
  valid_bookings_attendee_ids: number[];
  event_lecturers: EventLecturerRecordType[];
};

export enum EventStatusEnum {
  Draft = "DRAFT",
  Offered = "OFFERED",
  TakesPlace = "TAKES_PLACE",
  Finished = "FINISHED",
  Canceled = "CANCELED",
}

export enum EventFormatEnum {
  Online = "ONLINE",
  Onsite = "ONSITE",
  Hybrid = "HYBRID",
  Elearning = "E_LEARNING",
}

/** The leading source for lecturers and locations, for both output and editing. */
export enum ResourceSourceEnum {
  Event = "EVENT",
  Dates = "DATES",
}

export type EventMoodleCourseType = {
  id: number;
  name: string;
};

export type EventRecordType = {
  archived_at: string | null;
  attendees_max: number | null;
  attendees_min: number | null;
  cost_center: string | null;
  cost_object: string | null;
  project_number: string | null;
  created_at: string;
  custom_fields_data: CustomFieldDataType;
  dates: EventDateRecordType[];
  event_base: number;
  files: FileRecordType[];
  first_day: string | null;
  last_day: string | null;
  group_children: Identifier[] | EventGroupChildrenRecordType[];
  group_parents: Identifier[];
  id: number;
  id_number: string;
  inventory_account: string | null;
  is_bookable: boolean;
  is_public: boolean;
  format: EventFormatEnum;
  lecturers: Identifier[];
  location: Identifier | null;
  meta: EventMetadataType;
  moodle_courses: EventMoodleCourseType[];
  online_invite_text: string | null;
  prices: PriceRecordType[];
  registration_begin: string | null;
  registration_deadline: string | null;
  responsibilities: ResponsibilityRecordType[];
  rooms: number[];
  status: EventStatusEnum;
  updated_at: string;
  external_url: string;
  default_todo_list: number;
  event_feedback_form: EventFeedbackFormRecordType;
  todos: TodoRecordType[];
  lecturers_may_edit_attendance: boolean;
  display_in_portal: boolean;
  is_billable: boolean;
  resource_source: ResourceSourceEnum;
};

export type EventGroupChildrenRecordType = Omit<EventRecordType, "group_children"> & {
  group_children: Identifier[];
};

export type EventFeedbackFormRecordType = {
  id: Identifier;
  event: number;
  feedback_form: number;
  valid_until: string;
  reporting_visible_for_lecturers: boolean;
  url: string;
  feedback_form_name: string;
};

export type DashboardEventIdNameType = {
  id: Identifier;
  name: string;
};

export interface DashboardEventDateRecordType {
  day: string;
  end_time: string | null;
  end_datetime: string;
  id: Identifier;
  location: DashboardEventIdNameType | null;
  rooms: DashboardEventIdNameType[];
  start_time: string | null;
  start_datetime: string;
  created_at?: string;
  updated_at?: string;
  event?: DashboardEventRecordType;
}

export type DashboardEventRecordType = {
  id: Identifier | string;
  id_number: string;
  status: EventStatusEnum;
  event_name: string;
  event_type: string;
  event_id: Identifier;
  lecturers: PersonRecordType[];
  location_name?: string;
  dates: DashboardEventDateRecordType[];
  contact_persons: MinimalUserRecordType[];
  responsibilities: ResponsibilityRecordType[];
  attendees_min?: number;
  attendees_max?: number;
  all_valid_bookings_count?: number;
  unprocessed_enrollments_count?: number;
  all_waiting_bookings_count?: number;
  group_children: Identifier[];
  is_group_parent: boolean;
  meta?: EventMetadataType;
};

export type EventActionBarButtonsPropsType = {
  record: EventRecordType;
  className?: string;
};

export type EventRoomRecordType = {
  id: Identifier;
  created_at: string;
  identifier: string;
};

type NonStaffEventListMetaRecordType = {
  is_group_parent: boolean;
  event_base_name: string;
};

export type NonStaffEventListRecordType = {
  id: number;
  id_number: string;
  status: EventStatusEnum;
  dates: NonStaffEventDateRecordType[];
  location: EventLocationRecordType | null;
  meta: NonStaffEventListMetaRecordType;
  format: EventFormatEnum;
};

export type PortalCustomFieldDataType = {
  [slug: string]: {
    label: string;
    value: string | boolean | Date | number | number[];
    data_type: string;
  };
};

export type NonStaffEventRecordType = Omit<NonStaffEventListRecordType, "meta"> & {
  created_at: string;
  event_base: number;
  first_day: string | null;
  last_day: string | null;
  files: NonStaffFileRecordType[];
  lecturers: LecturerRecordType[];
  online_invite_text: string | null;
  prices: PriceRecordType[];
  contact_persons: PersonRecordType[];
  rooms: EventRoomRecordType[];
  external_url: string;
  event_feedback_form: EventFeedbackFormRecordType;
  can_lecturers_edit_attendance: boolean;
  group_children: NonStaffEventRecordType[];
  custom_fields_data: PortalCustomFieldDataType;
  custom_fields_meta?: CustomFieldMetaType;
  summary: string;
  meta: NonStaffEventListMetaRecordType & {
    event_base_files: NonStaffFileRecordType[];
  };
};

export type NonStaffEventDateRecordType = {
  day: string;
  end_time: string | null;
  end_datetime: string;
  id: Identifier;
  location: EventLocationRecordType;
  rooms: EventRoomRecordType[];
  start_time: string | null;
  start_datetime: string;
  format?: EventFormatEnum;
};
